import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../api/axiosConfig';
import {
  Container, Box, Typography, TextField, Button, Alert, CircularProgress, Paper, Avatar,
  InputAdornment, IconButton
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const PublicCompanyLogin = () => {
  const { publicId } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleRetry = () => {
    setError('');
    setPassword('');
  };

  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        const response = await axiosInstance.get(`/public/company/${publicId}`);
        setCompanyName(response.data.name);
      } catch (err) {
        setError('Cette page publique est introuvable ou désactivée.');
      } finally {
        setLoading(false);
      }
    };
    fetchCompanyInfo();
  }, [publicId]);

  // Mettre à jour le titre de la page dans le navigateur
  useEffect(() => {
    if (companyName) {
      document.title = `Planning de ${companyName}`;
    } else {
      document.title = 'Accès au Planning';
    }
  }, [companyName]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const response = await axiosInstance.post(`/public/company/${publicId}/login`, { password });
      sessionStorage.setItem(`public_token_${publicId}`, response.data.token);
      navigate(`/public/${publicId}/schedule`);
    } catch (err) {
      setError('Mot de passe incorrect.');
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Container component="main" maxWidth="xs">
      <Paper elevation={3} sx={{ marginTop: 8, padding: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component="h1" variant="h5" gutterBottom>
          Planning de {companyName}
        </Typography>
        {error ? (
          <>
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>
            {error === 'Mot de passe incorrect.' && (
              <Button
                onClick={handleRetry}
                fullWidth
                variant="contained"
                sx={{ mt: 1 }}
              >Réessayer</Button>
            )}
          </>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
              Veuillez saisir le mot de passe pour accéder au planning.
            </Typography>
            <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%' }}>
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Mot de passe"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="start"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
              >
                Accéder
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default PublicCompanyLogin;
